/**
 * @file src/api/collectionDataService.js
 * @description Collection-data client for the native collection mode (decisions/0004).
 *
 * Collection mode receives only the collection id in the URL and resolves the collection HERE, with the
 * session token — no host bridge. The backend authorizes per user and returns:
 *   - `roster`: ordered voice descriptors matching the realm roster shape (`getRosterFromUrl`), so the
 *     realm can boot them straight in (`voiceId`/`trackId`/`orbiterId`/`entangledWorldId`).
 *   - `permissions`: `{ canView, canEdit }` — collection mode gates edit affordances on `canEdit`.
 *   - `layout`: an optional saved slot arrangement (subtype + split ratios + focus) to restore.
 *
 * The real endpoint is mycelium's `GET {apiBase}/{version}/collections/{id}/public?hydrationLevel=1`
 * (cards only — the level-1 payload carries each item's `entity.miniCard`, which is all the drawer +
 * stages need to render fast at any collection size). Full per-item release hydration is NOT fetched up
 * front: the collection starts empty (arrange UX), so a release is fetched lazily on drag-to-stage, with
 * a bounded background warm-up of the first few drawer entries (see `createCollectionApp`). This replaced
 * the old fat `hydrationLevel=2` fetch, whose ~4.5s-per-item server hydration was paid up front for every
 * item whether or not it was ever staged. The same one root's Next BFF proxies to. Orbiters calls it
 * directly with the session token
 * via `httpClient` (its `resolveApiBase()` already yields `{base}/{version}`). When NO api base is
 * configured (unit tests / a bare local run), or `?/VITE_COLLECTION_STUB=1` forces it, we fall back to a
 * deterministic stub so the surface stays buildable + previewable. `saveCollectionLayout` is the one-shot
 * authed write for a rearranged layout, gated on `canEdit` by the caller.
 */
import { fetchJsonFromApi, resolveApiBase } from './httpClient.js';
import { requestEmbeddedAuthToken } from './dataManager/loaders.js';
import { resolveViewerAuth } from '../auth/viewerAuth.js';
import { getRoomGrantHeaders } from '../room/roomGrant.js';
import { normalizeDrawPinned } from '../room/handDraw.js';
import { sanitizeId } from '../session/sessionDescriptor.js';
import { createArrangementRevisions } from './arrangementRevisions.js';

// Force the local stub even when an api base exists (dev aid). Otherwise the stub is used only when no
// api base is configured (e.g. the test environment), so the browser preview hits the real endpoint.
const FORCE_STUB =
  typeof import.meta !== 'undefined' && import.meta.env
    ? import.meta.env.VITE_COLLECTION_STUB === '1'
    : false;

const BASE_ENDPOINT = '/collections';

/**
 * The write bookkeeping for the COLLECTION's own layout — what the server last confirmed it holds,
 * and which revision that was — so a save can state only what changed and cannot be overtaken by an
 * older one. See `arrangementRevisions.js`; a person's own arrangement keeps its own tracker in
 * `collectionArrangementService.js`, because it is a different document with its own revision line.
 */
const layoutWrites = createArrangementRevisions();

/**
 * Normalize one backend collection entry into the realm roster descriptor. The public-collection endpoint
 * returns mixed entries — `{ entityType: 'track'|'orbiter'|'world', entityId, ... }` (same shape root's
 * `use-mixed-collection-items` reads) — so we map by `entityType` onto the descriptor the realm boots
 * from (`trackId`/`orbiterId`/`entangledWorldId`), exactly like single-orbiter resolves `?trackId=` /
 * `?orbiterId=` / `?entangledWorldId=`. `voiceId` is the collection item id (stable, distinct per stage),
 * falling back to the entity id.
 * The optional `title`/`subtitle`/`image`/`entityType` are display-only metadata the arrange drawer
 * shows on its cards; the realm boot ignores them (it reads only the id fields), so they are safe to carry.
 * @returns {{voiceId:string, trackId:(string|null), orbiterId:(string|null), entangledWorldId:(string|null), entityType:(string|null), title:(string|null), subtitle:(string|null), image:(string|null)} | null}
 */
// Exported for queue mode: the queue endpoint returns the SAME
// collection-item envelopes, so the same normalizer parses them.
export function normalizeRosterEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;

  // Mycelium's collection item shape is `{ id, entityType, entityId, entity: { miniCard: {…} } }` — the
  // display fields live under `entity.miniCard`. Accept that, plus a flat/`miniCard`-at-top shape (stub /
  // URL descriptor), by resolving the miniCard from either place.
  const hydrated = entry.entity && typeof entry.entity === 'object' ? entry.entity : null;
  const mini =
    (hydrated?.miniCard && typeof hydrated.miniCard === 'object' && hydrated.miniCard) ||
    (entry.miniCard && typeof entry.miniCard === 'object' && entry.miniCard) ||
    hydrated ||
    null;

  // An archived/deleted entity arrives as an "unavailable" placeholder (title-less miniCard with
  // `unavailable: true`) — it can't boot (its release fetch errors) so it must never become a drawer
  // card or a prefetch. Root's collection view drops these the same way (`unavailable !== true`).
  if (entry.unavailable === true || entry.entity === null || mini?.unavailable === true) return null;

  const directTrack = sanitizeId(entry.trackId || entry.track_id || null);
  const directOrbiter = sanitizeId(entry.orbiterId || entry.orbiter_id || entry.engineId || null);
  const directWorld = sanitizeId(entry.entangledWorldId || entry.entangled_world_id || entry.worldId || null);

  // `entityType`/`entityId` sit on the collection item; `entry.id` is the ITEM id (the stable voiceId),
  // NOT the entity id — so it must not stand in for the entity id here.
  const entityType = String(entry.entityType || entry.type || mini?.entityType || '').toLowerCase();
  const entityId = sanitizeId(entry.entityId || entry.entity_id || mini?.entityId || null);

  let trackId = directTrack;
  let orbiterId = directOrbiter;
  let entangledWorldId = directWorld;
  if (entityId) {
    if (entityType === 'track') trackId = trackId || entityId;
    else if (entityType === 'orbiter') orbiterId = orbiterId || entityId;
    else if (entityType === 'world' || entityType === 'entangled-world') entangledWorldId = entangledWorldId || entityId;
    else if (!trackId && !orbiterId && !entangledWorldId) trackId = entityId; // untyped → assume track
  }

  if (!trackId && !orbiterId && !entangledWorldId) return null; // nothing bootable

  // voiceId = the stable collection item id (distinct per stage), else fall back to the entity id.
  // Root's own precedence, verbatim (`itemId ?? collectionItemId ?? _id ?? id`): a payload that carries
  // BOTH keys with different values has `_id` as the Mongo item id, and `id` can be the entity id — so
  // reading `id` first would write the wrong id to the remove endpoint.
  const itemId = sanitizeId(entry.itemId || entry.collectionItemId || entry._id || entry.id || null);
  const voiceId =
    sanitizeId(entry.voiceId || entry.voice_id || null) || itemId || orbiterId || trackId || entangledWorldId || entityId;
  // `hydrationLevel=2` items ALSO carry the full hydrated release response (under the
  // item's `hydrated.releaseResponse`; older payloads may be flat) — the SAME payload the
  // per-voice `/{entity}/{id}/release` call would re-fetch. Keep it (returned as `hydrated`
  // below) so the boot can prime the release caches instead of paying for it again per voice.
  const hydratedReleaseRaw = entry.hydrated && typeof entry.hydrated === 'object' ? entry.hydrated : null;
  const hydratedRelease =
    hydratedReleaseRaw && typeof hydratedReleaseRaw.releaseResponse === 'object' && hydratedReleaseRaw.releaseResponse
      ? hydratedReleaseRaw.releaseResponse
      : hydratedReleaseRaw;

  // Display-only metadata for the arrange drawer cards (the realm boot ignores it). Image tiers: the
  // miniCard exposes `imageSmall` (thumbnail); accept `image`/`imageMid` too. Subtitle mirrors root's
  // `normalizeStandardEntityMiniCard`: artist display-name(s) first, else the OWNER's name — an orbiter/
  // world carries no `artists`, so without the owner fallback its card would read blank. `title` is left
  // NULL when the miniCard has none (an unhydrated entity) — the card renders a friendly
  // "Untitled", never the raw id (voiceId is an ObjectId, so it must never stand in for the title).
  const title = firstString(mini?.title, entry.title, entry.name);
  const image = firstString(mini?.imageSmall, mini?.image, mini?.imageMid, entry.image, entry.coverImage, entry.thumbnail);
  const subtitle =
    firstString(entry.subtitle, mini?.subtitle, entry.artist) || artistNames(mini?.artists) || personName(mini?.owner);
  const isOfficial = mini?.isOfficial === true || entry.isOfficial === true;
  const displayType = entityType || (trackId ? 'track' : orbiterId ? 'orbiter' : entangledWorldId ? 'world' : null);
  // `itemId` travels SEPARATELY from `voiceId` even though the voiceId is usually the same string:
  // the item id is what the collection-items write endpoint removes by, and `voiceId` falls back to
  // an ENTITY id when the payload carries no item id (stub / URL descriptor). Removing by that
  // fallback would ask mycelium to delete an item that does not exist. Null = this entry has no
  // collection item behind it, so it cannot be removed from one.
  return { voiceId, itemId, trackId, orbiterId, entangledWorldId, entityType: displayType, title, subtitle, image, isOfficial, hydrated: hydratedRelease };
}

/**
 * The collection's cover, at the tier worth downloading for a thumbnail.
 *
 * The backend files collection art under `images['square-cover']` as `{ original, mid, small }` —
 * NOT as a flat `coverImage` string like an item's miniCard. `mid` first because the waiting room
 * draws it at roughly a hundred pixels and `original` is a full-size upload: on a phone that is a
 * multi-megabyte fetch competing with the pool load happening behind the scrim, for a picture
 * nobody is looking at closely. `small` is a fine fallback, `original` the last resort.
 */
function readCollectionCover(data) {
  const cover = data?.images?.['square-cover'];
  if (!cover || typeof cover !== 'object') return null;
  return firstString(cover.mid, cover.small, cover.original);
}

/** The first argument that is a string, else null (so a non-string field can't mask a later valid one). */
function firstString(...candidates) {
  for (const c of candidates) if (typeof c === 'string') return c;
  return null;
}

/** Join an `artists` array (miniCard shape) into a display subtitle — "Bruna Guarnieri, …". Null if none. */
function artistNames(artists) {
  if (!Array.isArray(artists)) return null;
  const names = artists.map((a) => firstString(a?.displayName, a?.username, a?.name)).filter(Boolean);
  return names.length ? names.join(', ') : null;
}

/** A single person's display name (miniCard `owner`/artist shape) — display name, else username. Null if none. */
function personName(person) {
  if (!person || typeof person !== 'object') return null;
  return firstString(person.displayName, person.username, person.name);
}

/**
 * Guarantee each stage gets a DISTINCT voiceId. The realm keys voices by voiceId, so a collection that
 * lists the same track/orbiter twice (a legitimate "two instances" case) would otherwise collapse to one
 * voice — only one stage renders/sounds, and focus mis-maps. The real backend should supply
 * distinct voiceIds; this is a client-side safety net (suffix the position on a repeat).
 */
function dedupeVoiceIds(entries) {
  const seen = new Set();
  return entries.map((entry, index) => {
    let voiceId = entry.voiceId;
    if (seen.has(voiceId)) voiceId = `${voiceId}-${index}`;
    seen.add(voiceId);
    return voiceId === entry.voiceId ? entry : { ...entry, voiceId };
  });
}

/**
 * Resolve `{ canView, canEdit }` from the collection response. Mycelium's `/collections/{id}/public`
 * returns a top-level `permissionsPayload` (a sibling of `collection`, NOT nested) that it already
 * computed from the viewer's Bearer token — `{ viewerRole, capabilities: { canView, canEdit, ... } }`.
 * We read `capabilities` directly and treat `viewerRole === 'owner'` as edit (mirroring root's
 * `capabilities.canEdit || isOwner`). A flat `{ canView, canEdit }` (the stub / a legacy shape) is
 * still honoured as a fallback. canEdit gates ONLY the persist control — everyone who can view gets the
 * full session-local arrange interaction.
 * @param {object|null} permissionsPayload the top-level `permissionsPayload` (preferred).
 * @param {object|null} fallback a flat `{ canView, canEdit }` shape (stub / legacy).
 */
function normalizePermissions(permissionsPayload, fallback) {
  const capabilities =
    permissionsPayload && typeof permissionsPayload === 'object' ? permissionsPayload.capabilities : null;
  if (capabilities && typeof capabilities === 'object') {
    const isOwner = permissionsPayload.viewerRole === 'owner';
    return {
      canView: capabilities.canView !== false, // viewable unless explicitly denied
      canEdit: capabilities.canEdit === true || isOwner,
    };
  }
  const canView = fallback?.canView !== false; // default to viewable unless explicitly denied
  const canEdit = fallback?.canEdit === true; // edit is opt-in — never assume
  return { canView, canEdit };
}

/**
 * The optional saved layout. Kept intentionally small: the subtype (visible slot count) + the three
 * split ratios the layout owns + the focused index. Absent/invalid fields fall back to the layout's
 * own defaults (subtype = roster length, ratios = 0.5, focus = 0), so a partial payload is safe.
 * @returns {{subtype?:number, splitY?:number, splitXPrimary?:number, splitXSecondary?:number, focusedIndex?:number} | null}
 */
/**
 * The saved studio arrangement, or null.
 *
 * `order` and `entryOrder` are the ARRANGEMENT and are kept as ids, not numbers: `order`
 * is positional — index = stage, `null` = a stage left deliberately empty — so its holes are
 * meaningful and survive; `entryOrder` is a list, so anything unusable in it is simply dropped.
 *
 * Nothing here checks that the ids still name something in this collection. They are collection-item
 * ids, and an item can be removed long after a layout naming it was saved; the restore resolves them
 * against the live roster and skips what it cannot find. Filtering here would need the roster this
 * function does not have.
 */
function normalizeLayout(layout) {
  if (!layout || typeof layout !== 'object') return null;
  const num = (v) => (Number.isFinite(v) ? v : undefined);
  const bool = (v) => (typeof v === 'boolean' ? v : undefined);
  const id = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const out = {
    // Which WRITE this stored arrangement is. Bookkeeping rather than arrangement: the studio never
    // reads it, but the write path needs the number the server holds so its own writes can be
    // ordered against it, and this is the only place a stored layout reaches this side.
    rev: Number.isInteger(layout.rev) && layout.rev >= 0 ? layout.rev : undefined,
    subtype: num(layout.subtype),
    splitY: num(layout.splitY),
    splitXPrimary: num(layout.splitXPrimary),
    splitXSecondary: num(layout.splitXSecondary),
    focusedIndex: num(layout.focusedIndex),
    // The panels around the stages: whether each was showing, and the size it was dragged to. Booleans
    // are taken only when they ARE booleans — `undefined` means "never said", which the studio reads as
    // its own default, and coercing would turn a missing field into a deliberate "closed".
    drawerOpen: bool(layout.drawerOpen),
    drawerHeight: num(layout.drawerHeight),
    order: Array.isArray(layout.order) ? layout.order.map(id) : undefined,
    entryOrder: Array.isArray(layout.entryOrder) ? layout.entryOrder.map(id).filter(Boolean) : undefined,
    // The stages playing something other than their card's own pairing. A stage is a TRIAD, and
    // `order` names only the card — without these a stage restores to its track plus the author's
    // default pairing rather than what was set up on it. Sparse, each entry naming its own stage,
    // so it cannot slide out of step with `order`; an entry that swaps nothing is not a swap.
    swaps: Array.isArray(layout.swaps)
      ? layout.swaps
        .filter((swap) => swap && Number.isInteger(swap.index) && swap.index >= 0)
        .map((swap) => {
          const shaped = { index: swap.index };
          if (id(swap.orbiterId)) shaped.orbiterId = id(swap.orbiterId);
          if (id(swap.entangledWorldId)) shaped.entangledWorldId = id(swap.entangledWorldId);
          return shaped;
        })
        .filter((swap) => swap.orbiterId || swap.entangledWorldId)
      : undefined,
  };
  return Object.values(out).some((v) => v !== undefined) ? out : null;
}

/** The first array among the candidates (the payload shape varies: roster/items/entries, nested or not). */
function firstArray(...candidates) {
  for (const c of candidates) if (Array.isArray(c)) return c;
  return [];
}

/** The collection's `subtype` string → the initial visible STAGE COUNT (how many placeholders to show). */
const SUBTYPE_COUNT = { 'i-orbiter': 1, 'ii-orbiters': 2, 'iii-orbiters': 3, 'iv-orbiters': 4 };

export function normalizeCollectionPayload(payload, fallbackId = null) {
  const data = payload?.collection ?? payload?.data ?? payload ?? {};
  const rawRoster = firstArray(data.roster, data.voices, data.items, data.entries, payload?.items, payload?.entries);
  const roster = dedupeVoiceIds(rawRoster.map(normalizeRosterEntry).filter(Boolean));
  // Prefer the top-level `permissionsPayload` (mycelium's authed shape); a flat `{ canView, canEdit }`
  // (stub / legacy) is the fallback.
  const permissions = normalizePermissions(
    payload?.permissionsPayload ?? data.permissionsPayload,
    data.permissions ?? data.viewerPermissions ?? payload?.permissions ?? payload?.viewerPermissions,
  );
  // The collection's `subtype` sets how many stage placeholders show by default (root uses it too). Fold
  // it into the layout as the initial subtype unless a saved layout already specifies one.
  const savedLayout = normalizeLayout(data.layout ?? data.savedLayout ?? payload?.layout);
  const subtypeCount = SUBTYPE_COUNT[String(data.subtype || '').toLowerCase()];
  const layout = subtypeCount ? { ...(savedLayout || {}), subtype: savedLayout?.subtype ?? subtypeCount } : savedLayout;
  return {
    collectionId: sanitizeId(data.collectionId || data.id || data._id || null) || fallbackId,
    roster,
    permissions,
    layout,
    // What the collection LOOKS like. Display-only, and read by exactly one surface: the waiting
    // room, which shows the collection you are about to play while the room fills up.
    name: firstString(data.collectionName, data.name, data.title),
    coverImage: readCollectionCover(data),
    // The room's sayable code, present only when this read carried a room grant. It comes from the
    // GRANT rather than the boot URL on purpose: the waiting room prints it as this room's code, and
    // a link is something anyone can hand you with any code written on it.
    roomJoinCode: firstString(payload?.roomJoinCode, data.roomJoinCode) || null,
    // The room's SHAPE, from the same place and for the same reason as the code above: how many
    // turntables it deals onto and how many people hold a seat were recorded on the grant at mint,
    // and the link carrying the same numbers is editable by anyone. Null outside a room, and on a
    // grant minted before shapes were recorded — the boot falls back to the URL only then.
    roomShape: readRoomShape(payload?.roomShape ?? data.roomShape),
    // The room's score, already resolved to a deadline + seat-grant table and frozen on the grant at
    // mint. Same provenance as the code and the shape: it rides with the grant, never with the link,
    // so nobody can rewrite which score a room is playing. Null outside a room and on an unscored
    // one — the boot then mints a room with no score, exactly as before.
    roomScore: readRoomScore(payload?.roomScore ?? data.roomScore),
    // The room's card draw — which cards each section deals onto the rail. Same provenance as the
    // three above: minted onto the grant, read here, never taken from the link.
    roomDraw: readRoomDraw(payload?.roomDraw ?? data.roomDraw),
  };
}

/**
 * The frozen score runtime, or null. The shape is CHECKED here and never edited: the score was
 * resolved and frozen onto the grant at mint precisely so that nobody downstream can change which
 * score a room is playing, and dropping a section here would hand the relay a different score from
 * the one on the grant while the lengths and grants around it still described the original. So a
 * score is either wholly usable and passes through untouched, or it is no score at all — the same
 * all-or-nothing rule the live read applies when the relay sends it back.
 */
function readRoomScore(raw) {
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.sections)) return null;
  if (raw.sections.length === 0) return null;
  const usable = raw.sections.every((section) => Number(section?.durationMs) > 0);
  return usable ? raw : null;
}

/** `{ decks, players }` as counts, or null. A partial or nonsense shape is no shape at all. */
function readRoomShape(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const count = (value) => {
    const n = Number.parseInt(value, 10);
    return Number.isInteger(n) && n >= 1 && n <= 4 ? n : null;
  };
  const decks = count(raw.decks);
  const players = count(raw.players);
  return decks == null && players == null ? null : { decks, players };
}

/**
 * The room's card DRAW, from the same place and for the same reason as the code, the shape and the
 * score: recorded on the grant at mint, never on the link. A scored room deals a small HAND of cards
 * per section instead of offering the whole collection — `composition` says how many of each kind,
 * and `pinned` is the entity list the piles are built from. That list is the grant's, deliberately:
 * the collection read re-filters by release readiness, so an entity archived mid-room would shorten
 * one player's pool and not the other's, and the two would deal different hands.
 *
 * Null outside a room and on a grant minted before the draw existed — the boot then puts the whole
 * collection on the rail, exactly as before.
 */
function readRoomDraw(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return {
    // Off unless the grant explicitly says on — the same posture as `resolveDrawConfig`, the model
    // default, and the relay. `!== false` would read a partial payload as ON and deal a hand nobody
    // switched on; real grants always carry an explicit boolean.
    enabled: raw.enabled === true,
    composition: raw.composition && typeof raw.composition === 'object' ? raw.composition : null,
    pinned: normalizeDrawPinned(raw.pinned),
  };
}

/** A deterministic local roster so collection mode is previewable before the real backend lands. */
function buildStubCollection(collectionId) {
  // Two real published tracks (same ids the single-orbiter demo boots) so the stub actually sounds +
  // textures. Swap/extend freely — this whole function disappears when the endpoint lands.
  // `itemId` carries the same value as `voiceId` here because a real collection item's id IS the
  // voiceId — the rail's removal writes by item id, so a stub without one could not exercise it.
  const stubRoster = [
    { voiceId: 'stub-voice-1', itemId: 'stub-voice-1', trackId: 'demo-track-1', orbiterId: null, entangledWorldId: null, entityType: 'track', title: 'Demo Track 1', subtitle: 'Plantasia', image: null },
    { voiceId: 'stub-voice-2', itemId: 'stub-voice-2', trackId: 'demo-track-2', orbiterId: null, entangledWorldId: null, entityType: 'track', title: 'Demo Track 2', subtitle: 'Plantasia', image: null },
  ];
  return {
    collectionId,
    roster: stubRoster,
    permissions: { canView: true, canEdit: true },
    layout: null,
    name: 'Demo Collection',
    coverImage: null,
  };
}

/**
 * Fetch a collection's roster + permissions + saved layout by id, with the session token.
 * @param {string} collectionId
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{collectionId:(string|null), roster:Array, permissions:{canView:boolean,canEdit:boolean}, layout:(object|null)}>}
 * @throws {Error & {status?:number, code?:string}} on no-access / not-found so the caller can render a
 *   message instead of a broken realm.
 */
export async function fetchCollectionData(collectionId, { signal } = {}) {
  const id = sanitizeId(collectionId);
  if (!id) {
    const error = new Error('collectionId is required to load a collection');
    error.code = 'invalid-collection-id';
    throw error;
  }

  // No backend configured (unit tests / bare local) or forced → deterministic stub.
  if (FORCE_STUB || !resolveApiBase()) {
    return buildStubCollection(id);
  }

  const { authToken } = await resolveViewerAuth();
  // In a room, present the grant here too: the backend then answers with the ROOM's card pool
  // (the creator's view of this collection) instead of this viewer's own, so every participant
  // boots the same set. Empty bag outside a room → the request stays byte-identical.
  const response = await fetchJsonFromApi(
    `${BASE_ENDPOINT}/${encodeURIComponent(id)}/public?hydrationLevel=1`,
    { method: 'GET', authToken, signal, headers: getRoomGrantHeaders() },
  );

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
    }
    // "This is a room and you have no account." Playing requires one, at both doors — the backend
    // says so with its own code, and it is kept distinct from the generic no-access because the
    // answer to it is different: send them to sign in and bring them back here.
    const body = await response.json().catch(() => null);
    const error = new Error(`Failed to load collection (status ${response.status})`);
    error.status = response.status;
    error.code =
      body?.code === 'signInRequired' ? 'sign-in-required'
      : response.status === 404 ? 'not-found'
      : 'no-access';
    throw error;
  }

  const payload = await response.json();
  const collection = normalizeCollectionPayload(payload, id);
  // Start this session's write counter above whatever revision the stored layout is at, so the
  // first save is not mistaken for one made before it. Only the NUMBER is taken — the layout itself
  // is not adopted as a write base, because a save must never build a delta on top of an
  // arrangement the server has not confirmed to THIS session (see `arrangementRevisions.js`).
  layoutWrites.observeStored(id, collection.layout);
  return collection;
}

/**
 * Remove ONE item from the collection — the card rail's "Remove from collection".
 *
 * The SAME endpoint and payload root's collection views already use: `PATCH /collections/{id}/items`
 * with `{ action: 'remove', itemId }`. Mycelium drops the item, re-indexes the rest and answers with
 * the updated collection; no new backend flow exists for this, and none is needed.
 *
 * `itemId` is the COLLECTION ITEM id (`normalizeRosterEntry`'s `itemId`) — never the entity id: the
 * same entity can sit in one collection more than once, and the backend matches on item `_id`.
 *
 * Authed as the VIEWER, and deliberately WITHOUT the room-grant headers `fetchCollectionData` sends:
 * a room grant is a READ grant over someone else's card pool, so editing the collection behind it is
 * not what the grant permits. Callers gate on `permissions.canEdit`; the backend re-checks and
 * answers 403 when it disagrees.
 *
 * @param {string} collectionId
 * @param {string} itemId The collection ITEM id to drop.
 * @returns {Promise<true>} on success; throws with `status`/`code` otherwise.
 */
export async function removeCollectionItem(collectionId, itemId) {
  const id = sanitizeId(collectionId);
  const item = sanitizeId(itemId);
  if (!id) throw new Error('collectionId is required to remove a collection item');
  if (!item) throw new Error('itemId is required to remove a collection item');

  // No backend configured (unit tests / bare local run) or forced → acknowledge the write, so the
  // stubbed preview still exercises the rail's removal. Mirrors `fetchCollectionData`'s stub gate.
  if (FORCE_STUB || !resolveApiBase()) return true;

  const { authToken } = await resolveViewerAuth();
  const response = await fetchJsonFromApi(`${BASE_ENDPOINT}/${encodeURIComponent(id)}/items`, {
    method: 'PATCH',
    authToken,
    body: { action: 'remove', itemId: item },
  });

  if (!response.ok) {
    // 401 ONLY: a 403 here is the backend's considered answer about edit permission (see above), not a
    // missing or stale token — asking the embed host to re-issue one would be noise on every denial.
    if (response.status === 401) {
      requestEmbeddedAuthToken();
    }
    const body = await response.json().catch(() => null);
    const error = new Error(`Failed to remove collection item (status ${response.status})`);
    error.status = response.status;
    // Mycelium's own codes (FORBIDDEN / ITEM_NOT_FOUND / …) when it sent one — the caller decides
    // what to say, and "it was already gone" reads differently from "you may not edit this".
    // Only the BODY may say `ITEM_NOT_FOUND`. A bare 404 is just as likely to be an undeployed route or
    // a collection that no longer exists, and the caller treats "the item was already gone" as success —
    // so inferring it from the status alone would report removals that never happened.
    error.code = body?.code || (response.status === 403 ? 'FORBIDDEN' : 'remove-failed');
    throw error;
  }

  return true;
}

/**
 * Persist a rearranged collection layout (authed write; caller gates on `canEdit`).
 *
 * ## It states what CHANGED, not the whole arrangement
 *
 * A studio arrangement used to be written by replacing the stored layout wholesale, so nudging one
 * divider re-sent the entire card rail — up to 500 collection-item ids — to say that one number
 * moved. A save now carries only the fields that differ from the arrangement the server last
 * confirmed (see `arrangementRevisions.js`), so a divider drag on a large collection sends a request
 * that does not carry the rail order at all. A delta is a PATCH to the same path — a different VERB,
 * so a deployment that predates it refuses the request instead of misreading the delta as a whole
 * arrangement and storing it as one.
 *
 * Every write also carries a REVISION, and the server applies one only when it is at least the
 * revision it already holds. Without that, two saves in flight could arrive in either order and the
 * older one would win — the failure this and the delta are two halves of.
 *
 * @param {string} collectionId
 * @param {object} layout the arrangement as it stands, in the shape the restore reads back
 * @param {{ immediate?: boolean }} [options] `immediate` is the LEAVING path (see below)
 * @returns {Promise<boolean>} true when persisted.
 */
export async function saveCollectionLayout(collectionId, layout, { immediate = false } = {}) {
  const id = sanitizeId(collectionId);
  if (!id) throw new Error('collectionId is required to save a collection layout');

  // No backend configured (unit tests / bare local run) or forced → acknowledge the write, so the
  // stubbed preview still exercises the arrange surface. Mirrors `removeCollectionItem`. No write is
  // opened: there is no server to confirm a base, so nothing may be measured against one either.
  if (FORCE_STUB || !resolveApiBase()) return true;

  const write = layoutWrites.begin(id, layout, { whole: immediate });
  // Nothing about the arrangement differs from what the server confirmed — a divider that settled
  // back where it started, or a geometry callback with nothing new in it. Already saved.
  if (!write) return true;

  return sendLayout(id, write, immediate);
}

/**
 * Issue one layout write and settle this side's bookkeeping from the answer.
 *
 * Split out so the one CORRECTION this path allows itself has somewhere to go: a write the server
 * refuses as stale (another device holds a newer arrangement) is re-issued once, above the revision
 * the server named and stating the whole arrangement again, because the base it was measured
 * against has just been shown to be wrong. That is a single answer to an authoritative "you were
 * behind", not a queue — chaining writes would let one hung request stall every save behind it.
 */
async function sendLayout(id, write, immediate, corrected = false) {
  // `immediate` is the LEAVING path (the studio is unloading). Two things change, and both are
  // required for the write to survive the navigation that triggered it:
  //   - `keepalive`, so the browser finishes the request after the document is gone.
  //   - no `await` before the fetch. Resolving the viewer's bearer is asynchronous, and a request
  //     still sitting in a microtask when the document unloads is simply dropped — `keepalive`
  //     cannot save a request that was never issued. Mycelium accepts the session cookie on its
  //     own (`credentials: 'include'`), exactly as the queue's own exit write relies on, so the
  //     bearer is what is given up rather than the write itself.
  //
  // Either way this is authed as the VIEWER and deliberately WITHOUT the room-grant headers
  // `fetchCollectionData` sends, for the same reason `removeCollectionItem` omits them: a room grant
  // is a READ grant over someone else's card pool, and rearranging their collection is not what it
  // permits. The caller also refuses to persist at all inside a room — decks are dealt, not arranged.
  const authToken = immediate ? null : (await resolveViewerAuth()).authToken;
  let response;
  try {
    response = await fetchJsonFromApi(`${BASE_ENDPOINT}/${encodeURIComponent(id)}/layout`, {
      // A delta is a PATCH to the same path, which a deployment that predates it does not answer.
      // That refusal is the point: it cannot misread the delta as a whole arrangement and store it
      // as one, so the two sides may be deployed — or rolled back — in either order.
      method: write.partial ? 'PATCH' : 'PUT',
      authToken,
      keepalive: immediate,
      body: { layout: write.layout },
    });
  } catch (error) {
    layoutWrites.fail(id, write);
    throw error;
  }

  if (!response.ok) {
    layoutWrites.fail(id, write);
    // This server has no PATCH for this record: it predates deltas, or has been rolled back behind
    // them. Nothing was stored and nothing was lost — the base is now unknown, so the next write
    // states the whole arrangement, and this one is re-stated as one straight away.
    if (write.partial && (response.status === 404 || response.status === 405)) {
      const replacement = layoutWrites.begin(id, write.full);
      if (!replacement || replacement.partial) return false;
      return sendLayout(id, replacement, immediate, corrected);
    }
    if (response.status === 401) requestEmbeddedAuthToken();
    const error = new Error(`Failed to save collection layout (status ${response.status})`);
    error.status = response.status;
    throw error;
  }

  // The leaving path gives up the answer along with the bearer: reading the body means awaiting it,
  // and the document is already going. Nothing follows this write, so nothing needs the base it
  // would have confirmed.
  if (immediate) return true;

  const body = await response.json().catch(() => null);
  if (body?.applied === false) {
    // The server holds a newer arrangement than the one this write was measured against.
    const retry = layoutWrites.reject(id, write, body?.rev ?? null);
    if (!retry || corrected) return false;
    const next = layoutWrites.begin(id, write.full);
    if (!next) return false;
    return sendLayout(id, next, false, true);
  }

  // The layout AS STORED becomes the base for the next delta — see `arrangementRevisions.js`.
  layoutWrites.confirm(id, write, body?.layout ?? null);
  return true;
}
